/**
 * Session lifecycle: access token custody and silent refresh scheduling.
 *
 * The access token is held in memory only. Persisting a JWT in localStorage
 * exposes it to any XSS on the page; the refresh token is expected to live in
 * an httpOnly + Secure + SameSite cookie that JavaScript cannot read, which is
 * why the refresh call relies on `credentials: 'include'` rather than a header.
 */
import { apiConfig } from '@/config'
import { clientLogger } from '@/services/telemetry'

export interface SessionUser {
  id: string
  email?: string
  role?: string
}

export interface SessionTokens {
  accessToken: string
  /** Absolute expiry in epoch milliseconds. */
  expiresAt: number
}

export interface SessionSnapshot {
  user: SessionUser | undefined
  accessToken: string | undefined
  expiresAt: number | undefined
}

type Listener = (snapshot: SessionSnapshot) => void

/** Refresh this far before expiry so an in-flight request never races it. */
const REFRESH_SKEW_MS = 60_000

export class SessionManager {
  private user: SessionUser | undefined
  private tokens: SessionTokens | undefined
  private refreshTimer: ReturnType<typeof setTimeout> | undefined
  private refreshInFlight: Promise<boolean> | undefined
  private readonly listeners = new Set<Listener>()

  snapshot(): SessionSnapshot {
    return {
      user: this.user,
      accessToken: this.tokens?.accessToken,
      expiresAt: this.tokens?.expiresAt,
    }
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  get isAuthenticated(): boolean {
    return Boolean(this.user && this.tokens && this.tokens.expiresAt > Date.now())
  }

  start(user: SessionUser, tokens: SessionTokens): void {
    this.user = user
    this.tokens = tokens
    this.scheduleRefresh()
    this.notify()
  }

  clear(): void {
    this.user = undefined
    this.tokens = undefined
    this.cancelRefresh()
    this.notify()
  }

  /** Token for the Authorization header, refreshed first when near expiry. */
  async getAccessToken(): Promise<string | undefined> {
    if (!this.tokens) return undefined
    if (this.tokens.expiresAt - REFRESH_SKEW_MS > Date.now()) return this.tokens.accessToken

    const refreshed = await this.refresh()
    return refreshed ? this.tokens?.accessToken : undefined
  }

  /**
   * Exchanges the refresh cookie for a new access token. Concurrent callers
   * share one request so a burst of 401s cannot stampede the endpoint.
   */
  refresh(): Promise<boolean> {
    if (this.refreshInFlight) return this.refreshInFlight

    const request = this.performRefresh().finally(() => {
      this.refreshInFlight = undefined
    })

    this.refreshInFlight = request
    return request
  }

  private async performRefresh(): Promise<boolean> {
    if (!apiConfig.baseUrl) {
      clientLogger.warn('Session refresh skipped: VITE_API_URL is not configured')
      return false
    }

    try {
      const response = await fetch(`${apiConfig.baseUrl}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: apiConfig.defaultHeaders,
      })

      if (!response.ok) {
        this.clear()
        return false
      }

      const payload = (await response.json()) as {
        user: SessionUser
        accessToken: string
        expiresIn: number
      }

      this.start(payload.user, {
        accessToken: payload.accessToken,
        expiresAt: Date.now() + payload.expiresIn * 1000,
      })
      return true
    } catch (error) {
      clientLogger.error('Session refresh failed', error)
      this.clear()
      return false
    }
  }

  private scheduleRefresh(): void {
    this.cancelRefresh()
    if (!this.tokens) return

    const delay = Math.max(this.tokens.expiresAt - Date.now() - REFRESH_SKEW_MS, 0)
    this.refreshTimer = setTimeout(() => {
      void this.refresh()
    }, delay)
  }

  private cancelRefresh(): void {
    if (this.refreshTimer === undefined) return
    clearTimeout(this.refreshTimer)
    this.refreshTimer = undefined
  }

  private notify(): void {
    const snapshot = this.snapshot()
    this.listeners.forEach((listener) => listener(snapshot))
  }
}

export const sessionManager = new SessionManager()
